import { useDraggable } from '@dnd-kit/core'
import { ChevronDown, ChevronRight, Folder, FolderOpen, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { type KnowledgeFolderItem } from '@/models/knowledgeFolder'

export const FOLDER_DRAG_PREFIX = 'folder:'

export function FolderRowContent({ folder }: { folder: KnowledgeFolderItem }) {
  return (
    <div className="bg-surface flex items-center gap-2 rounded-md border px-3 py-2 text-left">
      <Folder className="text-muted-foreground size-4 shrink-0" />
      <span className="truncate font-medium">{folder.name}</span>
    </div>
  )
}

type Props = {
  folder: KnowledgeFolderItem
  depth: number
  expanded: boolean
  childCount: number
  isOver: boolean
  onToggle: () => void
  onNewDocument: () => void
  onNewSubfolder: () => void
  onEdit: () => void
  onDelete: () => void
}

export function FolderRow({
  folder,
  depth,
  expanded,
  childCount,
  isOver,
  onToggle,
  onNewDocument,
  onNewSubfolder,
  onEdit,
  onDelete,
}: Props) {
  const draggable = useDraggable({
    id: `${FOLDER_DRAG_PREFIX}${folder.id}`,
    data: { type: 'folder', folder },
  })

  const Chevron = expanded ? ChevronDown : ChevronRight
  const FolderIcon = expanded ? FolderOpen : Folder

  return (
    <div
      style={{ paddingLeft: depth * 16 }}
      className={cn('touch-none transition-opacity', draggable.isDragging && 'opacity-40')}
    >
      <div
        className={cn(
          'group flex items-center gap-1 rounded-md pr-1 hover:bg-gray-50',
          isOver && 'bg-blue-50 ring-2 ring-blue-400'
        )}
      >
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? `Collapse ${folder.name}` : `Expand ${folder.name}`}
          aria-expanded={expanded}
          className="text-muted-foreground shrink-0 rounded p-1 hover:text-gray-900"
        >
          <Chevron className="size-4" />
        </button>
        <button
          type="button"
          ref={draggable.setNodeRef}
          {...draggable.attributes}
          {...draggable.listeners}
          onClick={() => !draggable.isDragging && onToggle()}
          className="flex min-w-0 flex-1 cursor-grab items-center gap-2 py-2 text-left active:cursor-grabbing"
        >
          <FolderIcon className="text-muted-foreground size-4 shrink-0" />
          <span className="truncate font-medium">{folder.name}</span>
          <span className="text-muted-foreground shrink-0 text-xs">{childCount}</span>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              aria-label={`Folder actions for ${folder.name}`}
              className="size-7 shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
            >
              <MoreHorizontal />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={onEdit}>Rename or describe</DropdownMenuItem>
            <DropdownMenuItem onSelect={onNewDocument}>New document here</DropdownMenuItem>
            <DropdownMenuItem onSelect={onNewSubfolder}>New subfolder</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem variant="destructive" onSelect={onDelete}>
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {expanded && folder.description && (
        <p className="text-muted-foreground truncate pb-1 pl-8 text-xs" title={folder.description}>
          {folder.description}
        </p>
      )}
    </div>
  )
}
